/**
 * Shared Ark Nova scoring / reference numbers that are explicit in the Polish
 * digital rulebook (ark-nova-instrukcja-pl.md). Board-printed scoring-area
 * values stay board-referential — do not invent them here.
 */

export const ARK_NOVA_SUPPORTED_PLAYER_COUNTS = [1, 2, 3, 4] as const;

export const arkNovaBuildCosts = {
  coinsPerHex: 2,
  label: '2 monety za każde pole zajmowane przez budynek',
} as const;

export const arkNovaPartnerZooDiscount = {
  coinsPerMatchingContinentIcon: 3,
  label:
    '−3 monety za każdą pasującą ikonę kontynentu przy posiadanym ogrodzie partnerskim (nie poniżej 0)',
} as const;

export const arkNovaHandLimit = {
  base: 3,
  withUniversity: 5,
  label: 'Limit ręki: 3 (5 po odpowiedniej uczelni); sprawdzany w przerwie',
} as const;

export const arkNovaCardsAction = {
  breakSpacesMoved: 2,
  snapMinStrength: 5,
  label:
    'Akcja Karty (strona podstawowa): przesuń przerwę o 2; przy sile ≥5 możesz zgarnąć 1 dowolną kartę z wystawki',
} as const;

export const arkNovaXToken = {
  strengthBonus: 1,
  possessionLimit: 5,
  label: 'Żeton X: +1 siły; limit posiadania 5',
} as const;

export const arkNovaDisplay = {
  cardCount: 6,
  label: 'Wystawka: 6 odkrytych kart; dostęp zależy od reputacji (poza zgarnięciem)',
} as const;

export const arkNovaActionPositions = [1, 2, 3, 4, 5] as const;

export const arkNovaEndGameSteps = [
  'Bazowa wartość z położenia znaczników atrakcyjności i ochrony (obszar na planszy głównej)',
  'Zachowana karta punktacji końcowej (+/−)',
  'Punkty końcowe ze sponsorów, zwierząt, mapy i innych efektów',
  'Kary',
] as const;

export const arkNovaTieBreakerStatus =
  'Remis: wygrywa remisujący gracz, który wsparł najwięcej projektów ochrony przyrody (policz znaczniki zdjęte z lewej strony swojej mapy zoo). Jeśli nadal jest remis, remisujący gracze wspólnie wygrywają.';

/** Worked example using board-area values the player reads from the main board. */
export const arkNovaScoringWorkedExample = {
  id: 'ark-scoring-example',
  title: 'Przykład punktowania końcowego',
  lines: [
    {
      label: 'Obszar punktowy na planszy (odczyt znaczników) — przykład +8',
      points: 8,
      boardReferential: true,
    },
    { label: 'Karta punktacji końcowej (przykład +4)', points: 4 },
    { label: 'Sponsor z punktacją końcową (przykład +2)', points: 2 },
    { label: 'Kara (przykład −1)', points: -1 },
  ],
  total: 13,
  note: 'Wartość obszaru punktowego zawsze odczytaj z planszy głównej przy swoich znacznikach — nie z pamięci.',
} as const;

export function scoringTablesSearchBlob(): string {
  return [
    'punktacja koniec gry atrakcyjność ochrona przyrody remis',
    arkNovaBuildCosts.label,
    arkNovaPartnerZooDiscount.label,
    arkNovaHandLimit.label,
    arkNovaCardsAction.label,
    arkNovaXToken.label,
    arkNovaDisplay.label,
    ...arkNovaEndGameSteps,
    arkNovaTieBreakerStatus,
    arkNovaScoringWorkedExample.title,
    ...arkNovaScoringWorkedExample.lines.map((l) => `${l.label} ${l.points}`),
    `suma ${arkNovaScoringWorkedExample.total}`,
    ...arkNovaScoringRecords.map(
      (r) =>
        `${r.element} ${r.scoring} ${r.conditions} ${r.exceptions} ${r.keywords.join(' ')}`
    ),
  ].join(' ');
}

export type ArkNovaScoringModuleId = 'base' | 'marine-worlds';

export type ArkNovaScoringRecord = {
  id: string;
  gameId: 'ark-nova';
  moduleId: ArkNovaScoringModuleId;
  category: 'end-game' | 'ongoing';
  element: string;
  timing: 'end-game' | 'mid-game' | 'both';
  scoring: string;
  conditions: string;
  exceptions: string;
  returnsMeeple: string;
  sourceStatus: 'verified' | 'component-dependent';
  verified: boolean;
  order: number;
  keywords: string[];
};

export const arkNovaScoringRecords: ArkNovaScoringRecord[] = [
  {
    id: 'ark-board-area',
    gameId: 'ark-nova',
    moduleId: 'base',
    category: 'end-game',
    element: 'Obszar punktowy (atrakcyjność × ochrona)',
    timing: 'end-game',
    scoring:
      'Bazowa wartość z położenia znaczników atrakcyjności i ochrony — odczytaj z obszaru punktowego na planszy głównej (nie z pamięci).',
    conditions: 'Koniec gry — pierwszy krok scoringu.',
    exceptions: 'Wartości pól obszaru są nadrukowane na planszy (component-dependent).',
    returnsMeeple: 'N/A',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 10,
    keywords: ['atrakcyjność', 'ochrona', 'plansza'],
  },
  {
    id: 'ark-end-card',
    gameId: 'ark-nova',
    moduleId: 'base',
    category: 'end-game',
    element: 'Karta punktacji końcowej',
    timing: 'end-game',
    scoring: 'Zachowana karta punktacji końcowej (+/−) — zgodnie z tekstem karty.',
    conditions: 'Po odczycie obszaru punktowego.',
    exceptions: '—',
    returnsMeeple: 'N/A',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 20,
    keywords: ['karta', 'punktacji'],
  },
  {
    id: 'ark-sponsors-animals',
    gameId: 'ark-nova',
    moduleId: 'base',
    category: 'end-game',
    element: 'Sponsory, zwierzęta, mapa',
    timing: 'end-game',
    scoring:
      'Punkty końcowe ze sponsorów, zwierząt, mapy i innych efektów — zgodnie z nadrukiem kart / efektów.',
    conditions: 'Po karcie punktacji końcowej.',
    exceptions: '—',
    returnsMeeple: 'N/A',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 30,
    keywords: ['sponsor', 'zwierzęta', 'mapa'],
  },
  {
    id: 'ark-penalties',
    gameId: 'ark-nova',
    moduleId: 'base',
    category: 'end-game',
    element: 'Kary',
    timing: 'end-game',
    scoring: 'Odejmij kary końcowe według obowiązujących efektów.',
    conditions: 'Ostatni krok kategorii scoringu.',
    exceptions: '—',
    returnsMeeple: 'N/A',
    sourceStatus: 'verified',
    verified: true,
    order: 40,
    keywords: ['kary'],
  },
  {
    id: 'ark-tie',
    gameId: 'ark-nova',
    moduleId: 'base',
    category: 'end-game',
    element: 'Remis',
    timing: 'end-game',
    scoring: arkNovaTieBreakerStatus,
    conditions: 'Równy wynik końcowy.',
    exceptions: 'Jeśli nadal remis — wspólne zwycięstwo.',
    returnsMeeple: 'N/A',
    sourceStatus: 'verified',
    verified: true,
    order: 50,
    keywords: ['remis', 'ochrona'],
  },
  {
    id: 'ark-marine-worlds',
    gameId: 'ark-nova',
    moduleId: 'marine-worlds',
    category: 'end-game',
    element: 'Marine Worlds — efekty scoringu',
    timing: 'end-game',
    scoring:
      'Dodatkowe źródła VP z kart / efektów Marine Worlds — zgodnie z nadrukiem; bez osobnego wymyślonego katalogu w aplikacji.',
    conditions: 'Dodatek Marine Worlds aktywny.',
    exceptions: 'Procedury dodatku w jednym rozdziale menu.',
    returnsMeeple: 'N/A',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 100,
    keywords: ['marine', 'worlds', 'morze'],
  },
];

export function filterArkNovaScoring(
  activeModuleIds: ArkNovaScoringModuleId[],
  options: { showAll?: boolean } = {}
): ArkNovaScoringRecord[] {
  if (options.showAll) {
    return arkNovaScoringRecords.slice().sort((a, b) => a.order - b.order);
  }
  const enabled = new Set(activeModuleIds);
  enabled.add('base');
  return arkNovaScoringRecords
    .filter((r) => enabled.has(r.moduleId))
    .sort((a, b) => a.order - b.order);
}

export function validateArkNovaScoringTables(): string[] {
  const errors: string[] = [];
  if (arkNovaBuildCosts.coinsPerHex !== 2) {
    errors.push('build cost must be 2 coins per hex');
  }
  if (arkNovaPartnerZooDiscount.coinsPerMatchingContinentIcon !== 3) {
    errors.push('partner zoo discount must be 3');
  }
  if (arkNovaHandLimit.base !== 3 || arkNovaHandLimit.withUniversity !== 5) {
    errors.push('hand limit must be 3 / 5');
  }
  if (arkNovaCardsAction.breakSpacesMoved !== 2) {
    errors.push('cards action must move break by 2');
  }
  if (arkNovaCardsAction.snapMinStrength !== 5) {
    errors.push('snap requires strength >= 5');
  }
  if (arkNovaXToken.possessionLimit !== 5) {
    errors.push('X token possession limit must be 5');
  }
  if (arkNovaDisplay.cardCount !== 6) {
    errors.push('display must have 6 cards');
  }
  if (arkNovaActionPositions.length !== 5) {
    errors.push('action positions must be 1–5');
  }
  const sum = arkNovaScoringWorkedExample.lines.reduce((s, l) => s + l.points, 0);
  if (sum !== arkNovaScoringWorkedExample.total) {
    errors.push(`worked example total mismatch ${sum}`);
  }
  const ids = new Set<string>();
  for (const r of arkNovaScoringRecords) {
    if (ids.has(r.id)) errors.push(`Duplicate ark scoring id ${r.id}`);
    ids.add(r.id);
  }
  const base = filterArkNovaScoring(['base']);
  if (base.some((r) => r.moduleId === 'marine-worlds')) {
    errors.push('Marine Worlds scoring visible without module');
  }
  if (!base.some((r) => r.id === 'ark-board-area')) {
    errors.push('Missing board-area scoring row');
  }
  const withMw = filterArkNovaScoring(['base', 'marine-worlds']);
  if (!withMw.some((r) => r.id === 'ark-marine-worlds')) {
    errors.push('Marine Worlds scoring missing when enabled');
  }
  return errors;
}
